import axios from 'axios';

export function uploadCompanyFont(companyId, font) {
  const data = new FormData();
  data.append('font', font);

  return axios
    .post(`/api/v1/settings/company/${companyId}/upload-font`, data)
    .then(res => res.data.data.url);
}

export function uploadFileAttachment(companyId, file) {
  const data = new FormData();
  data.append('files', file);

  return axios
    .post(`/api/v1/settings/company/${companyId}/files`, data)
    .then(res => res.data.data.url);
}

export function uploadEmailImage(companyId, file) {
  const data = new FormData();
  data.append('files', file);

  // This endpoint returns { url: [uri] } (an array), unlike the sibling
  // upload endpoints which return { url: uri }. The persisted value is the
  // bare string, so unwrap here to keep optimistic state a string — otherwise
  // a one-element array flows into the logo <img> src and breaks it.
  return axios.post(`/api/v1/settings/company/${companyId}/image`, data).then(res => {
    const { url } = res.data.data;
    return Array.isArray(url) ? url[0] : url;
  });
}

// Brand / menu-page image upload (card 61). field is one of
// 'headerImage' | 'logoImage' | 'footerImage'.
export function uploadBrandImage(companyId, field, file) {
  const data = new FormData();
  data.append('files', file);

  return axios
    .post(`/api/v1/settings/company/${companyId}/brand-image/${field}`, data)
    .then(res => res.data.data.url);
}

// AI-generate a brand image (card 94). field is 'headerImage' | 'footerImage'.
// The backend samples the company's active tours, generates the image, hosts it on
// the CDN and returns the URL (same shape as uploadBrandImage). `brand` carries the
// live palette from the Setup form so the image stays on-brand with unsaved edits.
export function generateBrandImage(companyId, field, brand) {
  return axios
    .post(`/api/v1/settings/company/${companyId}/brand-image/${field}/generate`, {
      brand
    })
    .then(res => res.data.data.url);
}

// Card 102 (experimental) — scan the operator's website and return a brand
// DRAFT (colours, headline, fonts, hosted logo URL). Nothing is persisted:
// the caller merges the draft into the Setup form for review + Save.
export function generateBrandingFromSite(companyId, url) {
  return (
    axios
      .post(`/api/v1/settings/company/${companyId}/branding/generate-from-site`, {
        url
      })
      // Card 265 — { branding, warning? }: warning is set when the address
      // answered with a "page not found" screen, so the draft is suspect.
      .then(res => ({
        branding: res.data.data.branding,
        warning: res.data.data.warning
      }))
  );
}

// Card Y5QiKDCL — AI-discoverability benchmark. Crawl the operator's site and
// return the scored checklist (DiscoverabilityResult). Read-only.
export function auditSiteDiscoverability(companyId, url) {
  return axios
    .post(`/api/v1/settings/company/${companyId}/site-audit/discoverability`, {
      url
    })
    .then(res => res.data.data);
}

// Current company customizationDetails (card 61 — brand config lives here).
export function getCompanyCustomisation() {
  return axios
    .get('/api/v1/settings/me/company')
    .then(res => res.data.data?.customizationDetails || {});
}

// Persist brand config (card 61) — merges into customizationDetails server-side,
// so it never wipes the booking/email fields owned by Customisation Settings.
export function updateCompanyBranding(companyId, branding) {
  return axios
    .put(`/api/v1/settings/company/${companyId}/branding`, branding)
    .then(res => res.data.data);
}

export function getCompanyLanguage() {
  return axios
    .get('/api/v1/settings/me/company')
    .then(res => res.data.data?.systemPreferencesDetails?.language || 'en');
}

export function updateCompanyFeatures(companyId, features) {
  return axios
    .put(`/api/v1/settings/company/${companyId}/features`, features)
    .then(res => res.data.data);
}

// Card 74 — enable/disable the weekly AI agent summaries and their email
// delivery. Dedicated route: the generic /features one needs a full
// postTipPage payload this toggle doesn't have.
export function updateAgentSummarySettings(companyId, settings) {
  return axios
    .put(`/api/v1/settings/company/${companyId}/agent-summaries`, settings)
    .then(res => res.data.data);
}

// Card 289 — the operator's share wording for tour photos. Dedicated route on
// purpose: posting tourPhotos through the generic /features one would replace
// the stored object and wipe `enabled`, switching the feature off for them.
export function updateTourPhotoShareCopy(companyId, copy) {
  return axios
    .put(`/api/v1/settings/company/${companyId}/tour-photo-share`, copy)
    .then(res => res.data.data);
}

export function updateExternalLink(companyId, externalLink) {
  return axios
    .put(`/api/v1/settings/company/${companyId}/features/externalLink`, externalLink)
    .then(res => res.data.data);
}

export function deleteExternalLink(companyId, linkId) {
  return axios
    .delete(`/api/v1/settings/company/${companyId}/features/externalLink/${linkId}`)
    .then(res => res.data.data);
}

export function updateCommissionSettings(companyId, commissionSettings) {
  return axios
    .put(`/api/v1/settings/company/${companyId}/commission`, commissionSettings)
    .then(res => res.data.data);
}
